import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, Spinner } from '@tkpod/ui';
import { api } from '@/lib/api';
import { optionActionMessage } from '@/components/catalog/options/option-action-message';

interface ProductOptionsSettings {
  blockEnabled: boolean;
  galleryEnabled: boolean;
  allowedMimeSuperset: string[];
  maxFileMbCeiling: number;
  imageToolsOnUploads: boolean;
  retentionDays: number;
  malwareScanRequired: boolean;
  surchargeMode: 'DISABLED' | 'PORTAL_ONLY' | 'SHOPIFY_CART_TRANSFORM';
}

export function ProductOptionsSettingsTab() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'product-options', 'settings'],
    queryFn: () =>
      api.get<{ data: ProductOptionsSettings }>('/api/v1/admin/product-options/settings'),
  });
  const [form, setForm] = useState<ProductOptionsSettings | null>(null);
  const [mimeText, setMimeText] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (form === null && query.data?.data) {
      setForm(query.data.data);
      setMimeText(query.data.data.allowedMimeSuperset.join(', '));
    }
  }, [form, query.data]);

  const save = useMutation({
    mutationFn: (body: ProductOptionsSettings) =>
      api.patch<{ data: ProductOptionsSettings }>('/api/v1/admin/product-options/settings', body),
    onSuccess: async (res) => {
      setForm(res.data);
      setMimeText(res.data.allowedMimeSuperset.join(', '));
      setSaveError(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['admin', 'product-options', 'settings'] });
    },
    onError: (err: unknown) => {
      setSaveError(optionActionMessage(err, 'Failed to save settings'));
    },
  });

  if (query.error && !form) {
    return (
      <Card>
        <CardHeader title="Product Options" description={DESCRIPTION} />
        <p className="mt-4 text-sm text-danger-700">
          {query.error instanceof Error ? query.error.message : 'Failed to load settings.'}
        </p>
      </Card>
    );
  }

  if (query.isLoading || !form) {
    return (
      <Card>
        <CardHeader title="Product Options" description={DESCRIPTION} />
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      </Card>
    );
  }

  const onToggle = (key: keyof ProductOptionsSettings) => (event: ChangeEvent<HTMLInputElement>) => {
    setSaved(false);
    setForm((current) => (current ? { ...current, [key]: event.target.checked } : current));
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const allowedMimeSuperset = mimeText
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
    const invalidMime = allowedMimeSuperset.find((item) => !MIME_TYPE.test(item));
    if (invalidMime) {
      setSaveError(`"${invalidMime}" is not a MIME type. Use values such as image/png.`);
      return;
    }
    const maxFileMbCeiling = wholeInRange(form.maxFileMbCeiling, 1, 2000);
    const retentionDays = wholeInRange(form.retentionDays, 1, 3650);
    if (maxFileMbCeiling == null || retentionDays == null) {
      setSaveError('File size and retention must be whole numbers inside the allowed range.');
      return;
    }
    setSaveError(null);
    save.mutate({ ...form, allowedMimeSuperset, maxFileMbCeiling, retentionDays });
  };

  return (
    <Card>
      <CardHeader title="Product Options" description={DESCRIPTION} />
      {!form.blockEnabled && (
        <p className="mt-4 rounded-md border border-warning-border bg-warning-muted p-4 text-sm text-warning">
          The storefront and portal blocks are off. Customers keep the theme's own add to cart.
        </p>
      )}
      <form onSubmit={onSubmit} className="mt-6 space-y-6">
        <Toggle
          label="Enable the options block"
          hint="Kill switch. Off rejects new submissions and uploads."
          checked={form.blockEnabled}
          onChange={onToggle('blockEnabled')}
        />
        <Toggle
          label="Gallery uploads"
          hint="When off, the gallery source is removed from every product block."
          checked={form.galleryEnabled}
          onChange={onToggle('galleryEnabled')}
        />
        <Toggle
          label="Image tools on uploads"
          hint="When off, background removal and upscale stay disabled on customer uploads."
          checked={form.imageToolsOnUploads}
          onChange={onToggle('imageToolsOnUploads')}
        />
        <Toggle
          label="Require a malware scan"
          hint="Files stay private until a scanner marks them clean. With no scanner configured, those uploads fail."
          checked={form.malwareScanRequired}
          onChange={onToggle('malwareScanRequired')}
        />
        <label className="block text-sm">
          <span className="text-text-primary">Allowed file types</span>
          <span className="block text-xs text-text-tertiary">
            Comma-separated MIME types. Leave empty to use PNG, JPEG, WebP, GIF and PDF. Archives and scripts are always rejected. SVG is allowed only if you list image/svg+xml.
          </span>
          <textarea
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
            rows={3}
            value={mimeText}
            onChange={(event) => {
              setSaved(false);
              setMimeText(event.target.value);
            }}
          />
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="text-text-primary">Max file size (MB)</span>
            <NumberDraft
              min={1}
              max={2000}
              value={form.maxFileMbCeiling}
              onChange={(maxFileMbCeiling) => {
                setSaved(false);
                setForm({ ...form, maxFileMbCeiling });
              }}
            />
          </label>
          <label className="block text-sm">
            <span className="text-text-primary">Keep unused uploads (days)</span>
            <NumberDraft
              min={1}
              max={3650}
              value={form.retentionDays}
              onChange={(retentionDays) => {
                setSaved(false);
                setForm({ ...form, retentionDays });
              }}
            />
          </label>
        </div>
        <label className="block text-sm">
          <span className="text-text-primary">Surcharge charging</span>
          <span className="block text-xs text-text-tertiary">
            Disabled stores the price and does not charge it. Portal orders charge whenever charging is on, including while Shopify checkout is selected. Shopify checkout charges the formula, the tier, and option deltas only after checkout pricing and the quantity check are enabled on the store.
          </span>
          <select
            className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
            value={form.surchargeMode}
            onChange={(event) => {
              setSaved(false);
              setForm({
                ...form,
                surchargeMode: event.target.value as ProductOptionsSettings['surchargeMode'],
              });
            }}
          >
            <option value="DISABLED">Disabled</option>
            <option value="PORTAL_ONLY">Portal orders only</option>
            <option value="SHOPIFY_CART_TRANSFORM">Shopify checkout</option>
          </select>
        </label>
        {saveError ? <p className="text-sm text-danger-700">{saveError}</p> : null}
        {saved ? <p className="text-sm text-text-secondary">Saved.</p> : null}
        <Button type="submit" loading={save.isPending}>
          Save
        </Button>
      </form>
    </Card>
  );
}

const MIME_TYPE = /^[a-z0-9][a-z0-9!#$&^_.+-]{0,126}\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/;

function wholeInRange(value: number, min: number, max: number): number | null {
  if (!Number.isInteger(value) || value < min || value > max) return null;
  return value;
}

const DESCRIPTION =
  'Platform switches for custom product options. These apply to every store.';

function NumberDraft({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    <input
      type="number"
      min={min}
      max={max}
      className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
      value={text ?? String(value)}
      onChange={(event) => {
        const raw = event.target.value;
        setText(raw);
        const next = Number(raw);
        if (Number.isInteger(next)) onChange(next);
      }}
      onBlur={() => setText(null)}
    />
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 rounded border-border"
      />
      <span>
        <span className="text-sm text-text-primary">{label}</span>
        <span className="block text-xs text-text-tertiary">{hint}</span>
      </span>
    </label>
  );
}
